/**
 * What an automation's stopped runs need from the host, in words, by the kind of step they stopped on: "2 emails
 * didn’t arrive". The list card and the automation's own page say it the same way.
 */
import type { ActionType } from "./config";
import { richToText } from "./tokens";
import type { AutomationFailedRun } from "./types";

const STOPPED: Partial<Record<ActionType, [string, string]>> = {
  email: ["email didn’t arrive", "emails didn’t arrive"],
  room: ["update didn’t post", "updates didn’t post"],
  webhook: ["handoff didn’t go through", "handoffs didn’t go through"],
  notify: ["team alert didn’t go out", "team alerts didn’t go out"],
  fact: ["tag didn’t change", "tags didn’t change"],
};

export function attentionWords(runs: Pick<AutomationFailedRun, "step_type">[]): string | null {
  if (!runs.length) return null;
  const kinds = new Set(runs.map((r) => r.step_type));
  const only = kinds.size === 1 ? [...kinds][0] : null;
  const words = only ? STOPPED[only] : undefined;
  const n = runs.length;
  const count = n.toLocaleString("en-US");
  if (words) return `${count} ${n === 1 ? words[0] : words[1]}`;
  return `${count} ${n === 1 ? "run stopped" : "runs stopped"}`;
}

/** The share of its emails that were opened (a click counts), as a whole percent; null before any went out. */
export function openedShare(e: { sent?: number; opened: number } | null | undefined): number | null {
  if (!e?.sent) return null;
  return Math.round((e.opened / e.sent) * 100);
}

/** What a tag, team alert, webhook or branch step did: "708 tagged", "197 notes", "63 checked · 21 have completed". */
export function actWords(
  kind: ActionType,
  act: { n: number; yes: number },
  step: { op?: string; ask?: { kind: string; tag?: string } } = {},
): string {
  const n = act.n.toLocaleString("en-US");
  switch (kind) {
    case "fact":
      return `${n} ${step.op === "remove" ? "untagged" : "tagged"}`;
    case "notify":
      return `${n} ${act.n === 1 ? "note" : "notes"}`;
    case "webhook":
      return `${n} handed off`;
    case "branch": {
      const yes = act.yes.toLocaleString("en-US");
      const ask = step.ask;
      const said = ask?.kind === "tag" && ask.tag ? `${act.yes === 1 ? "has" : "have"} ${ask.tag}` : ask?.kind === "notag" && ask.tag ? `${act.yes === 1 ? "doesn’t have" : "don’t have"} ${ask.tag}` : "yes";
      return `${n} checked · ${yes} ${said}`;
    }
    default:
      return "";
  }
}

/**
 * What an email step still needs before it can go to anyone: a subject, a message, or nothing. The builder will not
 * close its card, the save is refused and the engine sends nothing while either is missing, so nobody ever gets an
 * email with no words in it.
 */
export function emailNeeds(e: { subject?: string | null; body_html?: string | null; body?: string | null; preview?: string | null }): "subject" | "message" | null {
  if (!(e.subject ?? "").trim()) return "subject";
  const html = (e.body_html ?? "").trim();
  const words = html ? richToText(html) : (e.body ?? e.preview ?? "");
  return words.trim() ? null : "message";
}
